// src/pages/Content.tsx
import { useState, useEffect, useRef } from 'react';
import {
  X,
  Linkedin,
  FileText,
  Mail,
  FileOutput,
  Copy,
  Trash2,
  Check,
  Search,
  Maximize2,
  Upload,
  Settings,
  FolderKanban,
  CheckCircle2,
  Fingerprint,
  Zap
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { AppLayout } from '../components/layout/AppLayout';
import { db, auth, getContentDrafts, deleteContentDraft, ContentDraft, Project, StrategyDoc, getOrSeedStrategyDocs } from '../services/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  twitter: <X size={18} className="text-slate-900" />,
  linkedin: <Linkedin size={18} className="text-slate-800" />,
  blog: <FileText size={18} className="text-slate-800" />,
  newsletter: <Mail size={18} className="text-slate-800" />,
  other: <FileOutput size={18} className="text-slate-500" />
};

interface UploadedFile {
  id: string;
  name: string;
  content: string;
  compiledPrompt?: string;
  copied?: boolean;
}

export function Content() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'drafts' | 'import'>('drafts');

  // ── Drafts state ────────────────────────────────────────────────────────────
  const [drafts, setDrafts] = useState<ContentDraft[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [selectedDraft, setSelectedDraft] = useState<ContentDraft | null>(null);

  // ── Import state ─────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState<Project[]>([]);
  const [strategyDocs, setStrategyDocs] = useState<StrategyDoc[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedPersonaId, setSelectedPersonaId] = useState('');
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Buildlog — Content';
    async function loadAllData() {
      if (!auth.currentUser) return;
      try {
        const [draftsData, docsData, projectsSnapshot] = await Promise.all([
          getContentDrafts(auth.currentUser.uid),
          getOrSeedStrategyDocs(auth.currentUser.uid),
          getDocs(query(collection(db, 'projects'), where('userId', '==', auth.currentUser.uid), orderBy('updatedAt', 'desc')))
        ]);
        setDrafts(draftsData);
        setStrategyDocs(docsData);
        setProjects(projectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project)));
      } catch (error) {
        console.error('Could not load your content. Try refreshing.', error);
      } finally {
        setLoading(false);
      }
    }
    loadAllData();
  }, []);

  // ── Drafts handlers ─────────────────────────────────────────────────────────
  const handleCopyDraft = async (id: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteDraft = async (id: string) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await deleteContentDraft(id);
      setDrafts(drafts.filter(d => d.id !== id));
      if (selectedDraft?.id === id) setSelectedDraft(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const filteredDrafts = drafts.filter(draft => {
    const matchesSearch = draft.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = platformFilter === 'all' || draft.platform === platformFilter;
    return matchesSearch && matchesPlatform;
  });

  // ── Import handlers ──────────────────────────────────────────────────────────
  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSelectedVoiceId(project.voiceId || '');
      setSelectedPersonaId(project.personaId || '');
      setSelectedFrameworkId(project.frameworkId || '');
    } else {
      setSelectedVoiceId('');
      setSelectedPersonaId('');
      setSelectedFrameworkId('');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length === 0) return;
    const newFiles: UploadedFile[] = [];
    let filesProcessed = 0;
    uploadedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        newFiles.push({
          id: Math.random().toString(36).substring(7),
          name: file.name,
          content: ev.target?.result as string || ''
        });
        filesProcessed++;
        if (filesProcessed === uploadedFiles.length) {
          setFiles(prev => [...prev, ...newFiles]);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsText(file);
    });
  };

  const removeFile = (id: string) => setFiles(files.filter(f => f.id !== id));

  // ── E-E-A-T Prompt Compilation ───────────────────────────────────────────────
  const handleCompile = async () => {
    const voice = strategyDocs.find(d => d.id === selectedVoiceId);
    const persona = strategyDocs.find(d => d.id === selectedPersonaId);
    const framework = strategyDocs.find(d => d.id === selectedFrameworkId);
    const project = projects.find(p => p.id === selectedProjectId);

    if (!project) return alert('Choose a project before extracting signal.');

    setIsExtracting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    const compiledFiles = files.map(file => {
      const prompt = `You are an expert technical writer. I need you to extract signal from my raw build logs.

### YOUR RULES OF ENGAGEMENT (E-E-A-T STRICT)
**1. Your Brand Voice:** ${voice ? voice.title : 'Standard Professional'}
${voice ? voice.content : 'Be clear, concise, and highly technical.'}

**2. Your Professional Persona (The "Who"):** ${persona ? persona.title : 'Technical Expert'}
${persona ? persona.content : 'Write from the perspective of an experienced practitioner.'}
*CRITICAL DIRECTIVE: You must demonstrate first-hand experience. Speak as the person who actually executed this work. Do not sound like a marketer.*

**3. Your Story Framework (The "Why"):** ${framework ? framework.title : 'AEO Insight Arc'}
Please sequence the information strictly according to this framework:
${framework ? framework.content : 'Summarize the bottleneck, the specific architecture used, and the business outcome.'}
*CRITICAL DIRECTIVE: The primary goal is to help the reader solve a similar problem, not to brag.*

---
### PROJECT CONTEXT
**Project Goal:** ${project.description}
**Project Notes:** ${project.systemPrompt || 'Prioritize information density over generic advice.'}

---
### RAW LOGS TO PROCESS (The "How")
Below is my undeniable Proof of Work (unedited logs). Extract the highest "Information Gain" insights from this data. 
*CRITICAL DIRECTIVE: Do NOT invent facts. Do NOT hallucinate advice. Only extract insights directly supported by these raw logs.*

\`\`\`markdown
${file.content}
\`\`\`

**Transparency Requirement:** At the very bottom of your output, include this exact italicized text to comply with AI transparency standards:
*Signal extracted from raw development logs via Buildlog.*`.trim();

      return { ...file, compiledPrompt: prompt, copied: false };
    });

    setFiles(compiledFiles);
    setIsExtracting(false);
  };

  const copyPromptToClipboard = (fileId: string, text: string) => {
    navigator.clipboard.writeText(text);
    setFiles(files.map(f => f.id === fileId ? { ...f, copied: true } : f));
    setTimeout(() => {
      setFiles(files.map(f => f.id === fileId ? { ...f, copied: false } : f));
    }, 2000);
  };

  const voices = strategyDocs.filter(d => d.type === 'voice');
  const personas = strategyDocs.filter(d => d.type === 'persona' || (!d.type && d.title.startsWith('Persona:')));
  const frameworks = strategyDocs.filter(d => d.type === 'framework' || (!d.type && !d.title.startsWith('Persona:')));

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-40">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-[3px] border-slate-900 border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <style>{`
        @keyframes scan {
          0% { top: -10%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan { animation: scan 2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
      `}</style>

      <div className="space-y-8 pb-20">

        {/* ── Header ── */}
        <header className="space-y-6">
          <div className="border-b border-slate-200 pb-6">
            <h1 className="text-3xl font-bold text-slate-900 font-display tracking-tight">
              Your content
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Posts you've saved, and a tool to turn old notes into new ones.
            </p>
          </div>

          <div className="flex items-center gap-1 border-b border-slate-200">
            <button
              onClick={() => setActiveTab('drafts')}
              className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'drafts'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              Saved posts
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'import'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              Import notes
            </button>
          </div>
        </header>

        {/* ── Tab 1: Saved posts ── */}
        {activeTab === 'drafts' && (
          <div className="animate-in fade-in duration-300">
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Search your posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-all"
                />
              </div>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-all sm:w-48 capitalize font-medium"
              >
                <option value="all">All platforms</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="blog">Blog</option>
                <option value="newsletter">Newsletter</option>
                <option value="other">Other</option>
              </select>
            </div>

            {drafts.length === 0 ? (
              <div className="relative bg-slate-50 border border-slate-200 rounded-3xl p-12 text-center shadow-sm overflow-hidden">
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply"
                  style={{ backgroundImage: `url('/fingerprint-pattern.svg')`, backgroundSize: '100%', backgroundPosition: 'center' }}
                />
                <div className="relative z-10">
                  <Fingerprint className="mx-auto text-slate-300 mb-4" size={48} />
                  <h3 className="text-xl font-bold text-slate-900 font-display tracking-tight">Nothing saved yet</h3>
                  <p className="text-slate-500 mt-2 max-w-sm mx-auto text-sm leading-relaxed">
                    Posts you compile in Claude will appear here once you save them.
                  </p>
                </div>
              </div>
            ) : filteredDrafts.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-12 text-center shadow-sm">
                <Search className="mx-auto text-slate-300 mb-4" size={48} />
                <h3 className="text-xl font-bold text-slate-900 font-display">No results</h3>
                <p className="text-slate-500 mt-2 max-w-sm mx-auto text-sm">
                  Nothing matches that search. Try a different keyword or platform.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDrafts.map(draft => (
                  <div key={draft.id} className="bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden hover:shadow-md hover:border-slate-400 transition-all group">
                    <div className="bg-slate-50 border-b border-slate-100 px-5 py-3.5 flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-sm text-slate-900 capitalize font-display">
                        {PLATFORM_ICONS[draft.platform] || PLATFORM_ICONS['other']}
                        {draft.platform}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-slate-400 font-mono">
                          {draft.createdAt?.toDate ? draft.createdAt.toDate().toLocaleDateString() : 'Just now'}
                        </span>
                        <button
                          onClick={() => setSelectedDraft(draft)}
                          className="text-slate-400 hover:text-slate-900 transition-colors opacity-0 group-hover:opacity-100"
                          title="Read post"
                        >
                          <Maximize2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div
                      onClick={() => setSelectedDraft(draft)}
                      className="p-5 flex-1 prose prose-sm prose-slate max-w-none line-clamp-6 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    >
                      <ReactMarkdown>{draft.content}</ReactMarkdown>
                    </div>

                    <div className="border-t border-slate-100 p-3 flex items-center justify-end gap-2 bg-slate-50 mt-auto">
                      <button
                        onClick={() => handleDeleteDraft(draft.id)}
                        className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete post"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button
                        onClick={() => handleCopyDraft(draft.id, draft.content)}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-900 hover:text-slate-900 rounded-lg text-sm font-bold transition-all shadow-sm active:scale-95"
                      >
                        {copiedId === draft.id ? (
                          <><Check size={14} className="text-green-600" /><span className="text-green-600">Copied</span></>
                        ) : (
                          <><Copy size={14} /><span>Copy</span></>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab 2: Import notes ── */}
        {activeTab === 'import' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-in fade-in duration-300">

            {/* Left column: settings */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-6">
                <div className="flex items-center gap-2 pb-4 border-b border-slate-100">
                  <Settings size={18} className="text-slate-900" />
                  <h2 className="font-bold text-slate-900 font-display tracking-tight">Settings</h2>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
                    <FolderKanban size={12} /> Project
                  </label>
                  <select
                    value={selectedProjectId}
                    onChange={(e) => handleProjectSelect(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 focus:bg-white font-medium text-sm transition-all"
                  >
                    <option value="">Choose a project...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div className="space-y-5 pt-5 border-t border-slate-100">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Your rules</p>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-mono text-slate-500">Voice</label>
                    <select
                      value={selectedVoiceId}
                      onChange={(e) => setSelectedVoiceId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-slate-900 transition-colors"
                    >
                      <option value="">None — use default</option>
                      {voices.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-mono text-slate-500">Framework</label>
                    <select
                      value={selectedFrameworkId}
                      onChange={(e) => setSelectedFrameworkId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-slate-900 transition-colors"
                    >
                      <option value="">None — summarise only</option>
                      {frameworks.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-mono text-slate-500">Persona</label>
                    <select
                      value={selectedPersonaId}
                      onChange={(e) => setSelectedPersonaId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-slate-900 transition-colors"
                    >
                      <option value="">None — use default</option>
                      {personas.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Upload dropzone */}
              <div
                className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl p-8 text-center hover:border-slate-900 hover:bg-slate-100 transition-all group cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center mx-auto mb-4 group-hover:-translate-y-1 transition-transform">
                  <Upload size={20} className="text-slate-900" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">Upload your notes</h3>
                <p className="text-xs text-slate-500">Drag & drop multiple .md or .txt files</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".md,.txt,text/markdown,text/plain"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Right column: file queue */}
            <div className="lg:col-span-7 space-y-4 relative min-h-[400px]">

              {/* Scanning overlay */}
              {isExtracting && (
                <div className="absolute inset-[-1rem] z-20 bg-white/90 backdrop-blur-md rounded-3xl border border-slate-200 flex flex-col items-center justify-center overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                  <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply"
                    style={{ backgroundImage: "url('/fingerprint-pattern.svg')", backgroundSize: '120%', backgroundPosition: 'center' }}
                  />
                  <div
                    className="absolute left-0 w-full h-1 animate-scan pointer-events-none"
                    style={{ backgroundColor: 'var(--accent-primary)', boxShadow: '0 0 40px 4px var(--accent-primary)' }}
                  />
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-6 shadow-sm">
                      <Fingerprint size={32} className="text-accent animate-pulse" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 font-display mb-2">Applying E-E-A-T Constraints</h3>
                    <p className="text-xs font-mono text-slate-500 uppercase tracking-widest animate-pulse">Extracting signal...</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                <h2 className="text-[11px] font-bold font-mono uppercase tracking-wider text-slate-500">
                  Files ({files.length})
                </h2>
                <button
                  onClick={handleCompile}
                  disabled={files.length === 0 || !selectedProjectId || isExtracting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-accent text-white text-sm font-bold rounded-xl hover:bg-accent-hover transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isExtracting ? 'Extracting...' : 'Extract signal'} {!isExtracting && <Zap size={16} />}
                </button>
              </div>

              {files.length === 0 ? (
                <div className="py-24 text-center border-2 border-slate-200 rounded-3xl bg-slate-50 border-dashed shadow-sm">
                  <FileText size={32} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-[15px] font-medium text-slate-900">No files uploaded yet</p>
                  <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                    Upload your notes above and choose a project to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {files.map((file, index) => (
                    <div key={file.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-100">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="flex items-center justify-center w-6 h-6 rounded bg-white border border-slate-200 shadow-sm text-[10px] font-bold text-slate-500 shrink-0">
                            {index + 1}
                          </span>
                          <p className="text-sm font-bold text-slate-900 font-display truncate">{file.name}</p>
                        </div>

                        {!file.compiledPrompt ? (
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-slate-400 hover:text-red-500 p-1 bg-white rounded-md border border-slate-200 shadow-sm transition-colors"
                          >
                            <X size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => copyPromptToClipboard(file.id, file.compiledPrompt!)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm ${
                              file.copied
                                ? 'bg-green-50 border border-green-200 text-green-700'
                                : 'bg-slate-900 text-white hover:bg-slate-700'
                            }`}
                          >
                            {file.copied
                              ? <><CheckCircle2 size={14} /> Copied</>
                              : <><Copy size={14} /> Copy prompt</>
                            }
                          </button>
                        )}
                      </div>

                      <div className="p-5">
                        <textarea
                          readOnly
                          value={file.compiledPrompt || file.content}
                          className="w-full h-48 bg-slate-50 border border-slate-200 rounded-xl p-5 font-mono text-xs text-slate-600 leading-relaxed resize-none outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Full-view modal ── */}
      {selectedDraft && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2 font-bold text-lg text-slate-900 capitalize font-display">
                {PLATFORM_ICONS[selectedDraft.platform] || PLATFORM_ICONS['other']}
                {selectedDraft.platform} post
              </div>
              <button onClick={() => setSelectedDraft(null)} className="text-slate-400 hover:text-slate-900 transition-colors p-1">
                <X size={24} />
              </button>
            </div>

            <div className="p-8 overflow-y-auto flex-1 prose prose-slate max-w-none font-sans prose-headings:font-display prose-headings:text-slate-900">
              <ReactMarkdown>{selectedDraft.content}</ReactMarkdown>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => handleDeleteDraft(selectedDraft.id)}
                className="flex items-center gap-2 px-5 py-2.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors font-bold text-sm"
              >
                <Trash2 size={16} /> Delete
              </button>
              <button
                onClick={() => handleCopyDraft(selectedDraft.id, selectedDraft.content)}
                className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white font-bold text-sm rounded-xl hover:bg-slate-700 shadow-sm transition-all active:scale-95"
              >
                {copiedId === selectedDraft.id
                  ? <><Check size={16} /> Copied</>
                  : <><Copy size={16} /> Copy to clipboard</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}